import { Icon, Status } from '@assembly-js/design-system';
import { FormControl } from '@material-ui/core';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import clsx from 'clsx';
import { getIn, useFormikContext } from 'formik';
import moment, { Moment } from 'moment/moment';
import React, { ReactNode, useContext } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { DATE_FORMAT } from 'src/constants';
import { useDebounceInput } from 'src/hooks/useDebounceInput';
import { RegularCardBox } from 'src/legacy/components/Cards/RegularCardBox';
import PhoneNumberInput from 'src/legacy/components/Inputs/PhoneNumberInput';
import RowDivider from 'src/legacy/components/RowDivider';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import {
  BaseTextField,
  BaseTextFieldProps,
  ResizableTextField,
} from 'src/legacy/components/TextField';
import { RootState } from 'src/store';
import { GraySmall, NonHoverBorder, red } from 'src/theme/colors';

import { AsteriskIcon } from './AsteriskIcon';
import { FormBuilderPreviewContext } from './FormBuilderPreview';
import { InputsTypes, OptionsInfo, QuestionAnswer } from './formsTypes';
import { MultiChoicesSelectInput } from './MultiChoicesSelectInput';
import { QuestionFileDropzone } from './QuestionFileDropzone';
import { RichTextDescription } from './RichTextDescription';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: '24px',
    border: `1px solid ${NonHoverBorder}`,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  // when question is required and the question is not answered, the question border will be red.
  redBorderCard: {
    border: `1px solid ${red}`,
  },
  // disabled input hover state border override
  disabledInput: {
    '&:hover  fieldset': {
      borderColor: `${NonHoverBorder} !important`,
    },
  },
  // override MUI default .MuiOutlinedInput-adornedEnd padding-right (14px)
  datePickerAdornmentPadding: {
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: '4px',
    },
  },
}));

/**
 * @param questionConfigData: {QuestionType} The question configuration data.
 * @param questionIndex: {number} The question index is used to determine the question field values/errors states in the form.
 */
type QuestionAnswerCardProps = {
  questionAnswerData: QuestionAnswer;
  questionIndex: number;
  formId: string;
};

interface QuestionAnswerCardHeaderProps {
  title: ReactNode;
  description: string;
  type: InputsTypes;
  showRequiredAsterisk: boolean;
  showRequiredError: boolean;
  isQuestionEdited?: boolean;
}

/**
 * This component is responsible of rendering the question answer card header.
 * @param title: {string} The question title.
 * @param description: {string} The question description.
 * @param questionType: {InputsTypes} The question type. Used to determine the font type.
 * @returns
 */
export const QuestionAnswerCardHeader = ({
  title,
  description,
  type: questionType,
  showRequiredAsterisk,
  showRequiredError,
  isQuestionEdited,
}: QuestionAnswerCardHeaderProps) => {
  const { classes } = useStyles();
  const isTitleCard = questionType === InputsTypes.Title;
  const titleFontType = isTitleCard ? '24Medium' : '15Medium';
  const isClient = useSelector((state: RootState) => state.user.isClient);

  return (
    <div className={classes.header}>
      <div className="flex-1">
        <div className={classes.titleContainer}>
          <BaseTypography fontType={titleFontType}>{title} </BaseTypography>
          {/* show question deleted badge for internal user only  */}
          {!isClient && isQuestionEdited && (
            <Status label="Question Edited" status="warning" />
          )}
        </div>
        <RichTextDescription description={description} />
      </div>

      <span>
        {showRequiredError && (
          <BaseTypography
            fontType="13Regular"
            component="span"
            className="mr-1 text-error-primary"
          >
            Input required
          </BaseTypography>
        )}
        {showRequiredAsterisk && (
          // when the question is required, a red asterisk is displayed
          <AsteriskIcon
            style={{
              fontSize: '12px',
            }}
            className="text-error-primary"
          />
        )}
      </span>
    </div>
  );
};

const TextInputsTypes = [
  InputsTypes.ShortAnswer,
  InputsTypes.LongAnswer,
  InputsTypes.Email,
  InputsTypes.PhoneNumber,
];
export const MultiChoicesInputsTypes = [
  InputsTypes.SingleSelect,
  InputsTypes.MultiSelect,
];
const TextInputTypeToComponent = {
  [InputsTypes.ShortAnswer]: BaseTextField,
  [InputsTypes.LongAnswer]: ResizableTextField,
  [InputsTypes.Email]: BaseTextField,
};

const TextInputTypePlaceholder = {
  [InputsTypes.ShortAnswer]: 'Click here to input...',
  [InputsTypes.LongAnswer]: 'Click here to input...',
  [InputsTypes.PhoneNumber]: '(718) 123-4567',
  [InputsTypes.Email]: 'example@example.com',
};

type QuestionAnswerInputProps = {
  type: InputsTypes;
  options?: OptionsInfo[];
  answerType?: 'single' | 'multi'; // used for multi choices input types
  questionId: string;
  readOnly?: boolean;
  identityId?: string;
  questionIndex: number;
} & Partial<BaseTextFieldProps>;

const isTextType = (
  type: InputsTypes,
): type is
  | InputsTypes.ShortAnswer
  | InputsTypes.LongAnswer
  | InputsTypes.Email
  | InputsTypes.PhoneNumber => TextInputsTypes.includes(type);

/**
 * This component is responsible of rendering the question answer input.
 * Based on the question type different input components are rendered.
 * e.g. for a question of type 'text' a text input component is rendered.
 * @returns {JSX.Element}  The question answer input.
 */
export const QuestionAnswerInput = ({
  name,
  type: questionType,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  options = [],
  questionId,
  readOnly = false,
  identityId,
  formId,
  questionIndex,
}: QuestionAnswerInputProps & {
  formId?: string;
}) => {
  const formikContext = useFormikContext<{
    questionAnswers: QuestionAnswer[];
  }>();
  const { classes } = useStyles();
  const [datePickerErr, setDatePickerErr] = React.useState<string>('');
  const [datePickerValue, setDatePickerValue] = React.useState<Moment | null>(
    () => {
      if (questionType === InputsTypes.Date && typeof value === 'string') {
        const displayDate = moment(value, DATE_FORMAT, true);
        if (displayDate.isValid()) return displayDate;

        const canonicalDate = moment(value, 'YYYY-MM-DD', true);
        if (canonicalDate.isValid()) return canonicalDate;
      }
      return null;
    },
  );
  const { isFormBuilderPreview } = useContext(FormBuilderPreviewContext);

  const handleDateChange = (date: Moment | null) => {
    setDatePickerValue(date);
    if (date && !date.isValid()) {
      setDatePickerErr('Enter a valid date.');
      return;
    } else if (!date) {
      formikContext.setFieldValue(
        `questionAnswers[${questionIndex}].responseValue`,
        '',
      );
    }

    if (date && date.isValid() && formikContext) {
      const valStr = moment(date).format(DATE_FORMAT);

      formikContext.setFieldValue(
        `questionAnswers[${questionIndex}].responseValue`,
        valStr,
      );
    }

    formikContext.setFieldError(
      `questionAnswers[${questionIndex}].responseValue`,
      '',
    );
    setDatePickerErr('');
  };

  const inputComponent = React.useMemo(() => {
    // if the question type is a text input type, render the appropriate input component
    // which would be either a BaseTextField or a ResizableTextField.
    if (isTextType(questionType)) {
      return (
        <TextField
          type={questionType}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange ?? (() => {})}
          onBlur={onBlur}
          name={name}
          readOnly={readOnly}
          error={error}
          helperText={helperText}
        />
      );
    }

    if (questionType === InputsTypes.Date) {
      if (readOnly && !isFormBuilderPreview) {
        // on form response detail page, we don't show date picker but instead just show submitted date in text field
        return (
          <BaseTextField
            value={value}
            fullWidth
            variant="outlined"
            onBlur={onBlur}
            name={name}
            sizeVariant="medium"
            placeholder={DATE_FORMAT}
            disabled={readOnly}
            InputProps={{
              readOnly,
              classes: {
                disabled: classes.disabledInput,
              },
            }}
          />
        );
      } else {
        return (
          <FormControl fullWidth className={classes.datePickerAdornmentPadding}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                disabled={readOnly}
                format={DATE_FORMAT}
                value={datePickerValue}
                onChange={handleDateChange}
                readOnly={readOnly}
                // required because the `textField` slot renders a plain <input />; MUI X v7+ defaults to PickersSectionList
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: BaseTextField,
                  openPickerIcon: () => (
                    <Icon
                      icon="Calendar"
                      className={`w-[18px] h-[18px] text-[${GraySmall}]`}
                    />
                  ),
                }}
                slotProps={{
                  textField: {
                    name: `questionAnswers[${questionIndex}].responseValue`,
                    placeholder: DATE_FORMAT,
                    variant: 'outlined',
                    margin: 'none',
                    error: error || Boolean(datePickerErr),
                    helperText: helperText || datePickerErr,
                    onBlur,
                  },
                  // MUI X v7 defaults openPickerButton to edge="end" (margin-right: -12px); disable so paddingRight is honored
                  openPickerButton: { edge: false },
                }}
              />
            </LocalizationProvider>
          </FormControl>
        );
      }
    }

    // if the question type is a multi choices input type (single select or multi select)
    if (MultiChoicesInputsTypes.includes(questionType)) {
      return (
        <MultiChoicesSelectInput
          options={options}
          type={questionType}
          onChange={onChange}
          onBlur={onBlur}
          name={name}
          value={value as string[]}
          readOnly={readOnly}
          questionIndex={questionIndex}
        />
      );
    }

    if (questionType === InputsTypes.FileUpload) {
      return (
        <QuestionFileDropzone
          questionId={questionId}
          fieldName={name || ''}
          value={value as string | string[]}
          readOnly={readOnly}
          identityId={identityId}
          formId={formId}
        />
      );
    }

    return null;
  }, [
    questionType,
    value,
    error,
    helperText,
    readOnly,
    options,
    datePickerValue,
    datePickerErr,
  ]);

  return inputComponent;
};

const TextField = ({
  name,
  value,
  onChange,
  onBlur,
  readOnly,
  type,
  error,
  helperText,
}: Required<Pick<QuestionAnswerInputProps, 'onChange'>> &
  Pick<QuestionAnswerInputProps, 'name' | 'onBlur' | 'readOnly'> & {
    value: string;
    type:
      | InputsTypes.LongAnswer
      | InputsTypes.ShortAnswer
      | InputsTypes.Email
      | InputsTypes.PhoneNumber;
    error?: boolean;
    helperText?: React.ReactNode;
  }) => {
  const { classes } = useStyles();
  const [val, handleChange] = useDebounceInput(value, onChange);

  if (type === InputsTypes.PhoneNumber) {
    return (
      <PhoneNumberInput
        value={val}
        onChange={(phoneValue: string) => {
          const newEvent = {
            target: { name: name || '', value: phoneValue },
          } as unknown as React.ChangeEvent<HTMLInputElement>;
          handleChange(newEvent);
        }}
        onBlur={onBlur}
        name={name}
        sizeVariant="medium"
        placeholder={TextInputTypePlaceholder[type]}
        disabled={readOnly}
        error={error}
        helperText={helperText}
        // TODO: remove !important once MUI v5 is stable — JSS/TSS specificity currently conflicts with Tailwind
        className="[&_.Mui-error_fieldset]:!border-[#CC0000]"
      />
    );
  }

  const InputComponent = TextInputTypeToComponent[type];

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(event);
  };

  return (
    <InputComponent
      value={val}
      fullWidth
      variant="outlined"
      onChange={handleInputChange}
      onBlur={onBlur}
      name={name}
      sizeVariant="medium"
      placeholder={TextInputTypePlaceholder[type]}
      disabled={readOnly}
      error={error}
      helperText={helperText}
      InputProps={{
        readOnly,
        classes: {
          disabled: classes.disabledInput,
        },
      }}
    />
  );
};

const inlineErrorQuestionTypes = [
  InputsTypes.ShortAnswer,
  InputsTypes.LongAnswer,
  InputsTypes.PhoneNumber,
  InputsTypes.Email,
  InputsTypes.Date,
];

/**
 * This component is responsible of rendering the question answers/responses
 * card. This is used in the form response submission page as well as the
 * form response view page.
 * @returns {JSX.Element}  The question answer card.
 */
export const QuestionAnswerCard = ({
  questionAnswerData,
  questionIndex,
  formId,
}: QuestionAnswerCardProps) => {
  const { values, handleChange, handleBlur, errors, touched } =
    useFormikContext<{
      questionAnswers: QuestionAnswer[];
    }>();
  const {
    questionId,
    title: questionTitle,
    description,
    type: questionType,
    isRequired,
    options,
  } = questionAnswerData;
  const touch = getIn(
    touched,
    `questionAnswers[${questionIndex}].responseValue`,
  );
  const error = getIn(
    errors,
    `questionAnswers[${questionIndex}].responseValue`,
  );

  // required error is already shown inline with the field, so don't need to show in the header again
  const showRequiredError =
    isRequired &&
    touch &&
    error &&
    !inlineErrorQuestionTypes.includes(questionType);

  return (
    <RegularCardBox
      // TODO: remove !important once MUI v5 is stable — JSS/TSS specificity currently conflicts with Tailwind
      className={clsx('p-6 border border-solid', {
        '!border-error-primary': showRequiredError,
        '!border-non-hover-border': !showRequiredError,
      })}
    >
      <QuestionAnswerCardHeader
        title={questionTitle}
        description={description ?? ''}
        type={questionType}
        showRequiredAsterisk={isRequired}
        showRequiredError={showRequiredError}
      />
      {questionType !== InputsTypes.Title && (
        <RowDivider mt={2.5} mb={0} noLine />
      )}
      <QuestionAnswerInput
        questionId={questionId}
        type={questionType}
        value={values.questionAnswers?.[questionIndex]?.responseValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(error && touch)}
        helperText={error && touch ? error : ''}
        name={`questionAnswers[${questionIndex}].responseValue`}
        questionIndex={questionIndex}
        options={options}
        formId={formId}
      />
    </RegularCardBox>
  );
};
